import { useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Art } from '../components/Art';
import { BottomNav, Button, Tag } from '../components/ui';
import { Icon } from '../components/Icon';
import { Screen } from '../components/Screen';
import { Stage } from '../components/Stage';
import { header_your } from '../art/art';
import { useApp } from '../lib/app';
import { clockParts, dayLabel, monthName, rupees } from '../lib/format';
import { shortName } from '../lib/places';
import type { Ride } from '../lib/types';
import { words } from '../lib/vehicles';
import { friendly } from '../lib/errors';
import { useT } from '../lib/i18n';
import { EmptyArt } from '../art/Empty';
import { rewardValue } from '../lib/rewards';
import type { Rewards } from '../lib/types';
import type { StringKey } from '../lib/strings';

const ENDED = ['CANCELLED_BY_RIDER', 'CANCELLED_BY_DRIVER', 'CANCELLED_BY_ADMIN', 'EXPIRED'];

export function Trips() {
  const { api, setDraft } = useApp();
  const t = useT();
  const nav = useNavigate();
  const [rides, setRides] = useState<Ride[] | null>(null);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [rewards, setRewards] = useState<Rewards | null>(null);

  useEffect(() => {
    let live = true;
    api.rides(1, 100).then(
      (p) => {
        if (!live) return;
        setError(null);
        setRides(p.items);
        setTotal(p.total);
      },
      (e) => live && setError(friendly(e, t)),
    );
    api.rewards().then((r) => live && setRewards(r), () => undefined);
    return () => {
      live = false;
    };
  }, [api, attempt, t]);

  const completed = (rides ?? []).filter((r) => r.status === 'COMPLETED');
  const kmTotal = Math.round(completed.reduce((s, r) => s + r.distanceMeters, 0) / 1000);
  const first = completed.length ? completed[completed.length - 1] : null;
  const shown = (rides ?? []).filter((r) => r.status === 'COMPLETED' || ENDED.includes(r.status));
  const ready = rewards?.available[0] ?? null;

  const again = (r: Ride) => {
    setDraft({ pickup: r.pickup, drop: r.drop, quote: null, vehicleType: r.vehicleType });
    nav({ to: '/choose' });
  };

  return (
    <Screen name="your-trips" label={t('trips.title')}>
      <div className="zr-scroll">
        <Stage top={0} height={100}>
          <Art piece={header_your} />
        </Stage>
        <div style={{ position: 'relative', margin: 'calc(var(--sat) + 18px) 20px 0' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
            <h1 style={{ margin: 0, fontSize: 34, lineHeight: '38px', fontWeight: 900, letterSpacing: '-0.035em' }}>{t('trips.title')}</h1>
            {ready && (
              <span data-testid="reward-ready" title={t('trips.rewardsReady', { title: ready.title })} style={{ flex: 'none', display: 'flex' }}>
                <Tag style={{ height: 28, borderRadius: 14, gap: 5, fontSize: 12.5, animation: 'zr-popin .3s var(--ease-out) both' }}>
                  <Icon name="star" size={13} />
                  {t('pay.rewardOff', { amount: rewardValue(ready) })}
                </Tag>
              </span>
            )}
          </div>
          <p style={{ margin: '6px 0 0', fontSize: 15, lineHeight: '20px', fontWeight: 500, color: 'var(--ink2)', fontVariantNumeric: 'tabular-nums' }}>
            {rides == null
              ? (error ?? t('common.loading'))
              : completed.length
                ? t('trips.summary', { rides: t.n('common.rides', completed.length), km: kmTotal, month: monthName(first!.requestedAt) })
                : total
                  ? t('trips.noneDone')
                  : t('trips.empty')}
          </p>

          {error && rides == null && (
            <Button variant="secondary" style={{ marginTop: 16 }} onClick={() => setAttempt((n) => n + 1)}>
              {t('common.retry')}
            </Button>
          )}
          {rides != null && total === 0 && (
            <div className="zr-fadein" style={{ marginTop: 18, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <EmptyArt kind="trips" width={180} />
              <Button style={{ marginTop: 18 }} onClick={() => nav({ to: '/search' })} iconAfter="arrow">
                {t('trips.emptyCta')}
              </Button>
            </div>
          )}
          <div className="zr-stagger" style={{ marginTop: 20, display: 'flex', flexDirection: 'column', gap: 10 }}>
            {rides == null && !error && [0, 1, 2, 3].map((i) => <span key={i} aria-hidden="true" style={{ height: 108, borderRadius: 20, background: 'var(--s1)', animation: `zr-breathe 1.2s ${i * 0.1}s ease-in-out infinite` }} />)}
            {shown.map((r) => {
              const [hm, ap] = clockParts(r.requestedAt);
              const dl = dayLabel(r.requestedAt);
              const day = dl === 'TODAY' ? t('trips.today') : dl === 'YESTERDAY' ? t('trips.yesterday') : dl;
              const ok = r.status === 'COMPLETED';
              const who = r.driver?.firstName;
              const line = [words(t, r.vehicleType).label, who, ok ? (r.myRating ? t('trips.youGave', { n: r.myRating }) : t('trips.notRated')) : t(`trips.${r.status}` as StringKey), ok && r.paymentStatus === 'PENDING' ? t('trips.cashDue') : null]
                .filter(Boolean)
                .join(' · ');
              return (
                <article key={r.id} data-testid="trip" style={{ position: 'relative', height: 108, borderRadius: 20, background: 'var(--s1)', display: 'flex', flex: 'none' }}>
                  <div style={{ flex: 'none', width: 84, boxSizing: 'border-box', padding: '16px 0 0 16px' }}>
                    <div style={{ fontSize: 11, lineHeight: '14px', fontWeight: 700, letterSpacing: '0.1em', color: dl === 'TODAY' ? 'var(--acText)' : 'var(--ink3)' }}>{day}</div>
                    <div style={{ marginTop: 4, fontSize: 15, lineHeight: '20px', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{hm}</div>
                    <div style={{ fontSize: 12, lineHeight: '16px', fontWeight: 500, color: 'var(--ink3)' }}>{ap}</div>
                  </div>
                  <span aria-hidden="true" style={{ position: 'absolute', left: 84, top: 14, bottom: 14, borderLeft: '1.5px dashed var(--line2)' }} />
                  <span aria-hidden="true" style={{ position: 'absolute', left: 75, top: -10, width: 20, height: 20, borderRadius: 10, background: 'var(--bg)' }} />
                  <span aria-hidden="true" style={{ position: 'absolute', left: 75, bottom: -10, width: 20, height: 20, borderRadius: 10, background: 'var(--bg)' }} />
                  <div style={{ flex: 1, minWidth: 0, padding: '14px 16px 0 18px' }}>
                    <div style={{ fontSize: 15, lineHeight: '20px', fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {shortName(r.pickup.address)} → {shortName(r.drop.address)}
                    </div>
                    <div style={{ marginTop: 2, fontSize: 13, lineHeight: '18px', fontWeight: 500, color: 'var(--ink2)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{line}</div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <button type="button" onClick={() => again(r)} aria-label={`${t('trips.again')}: ${shortName(r.pickup.address)} → ${shortName(r.drop.address)}`} style={{ marginLeft: -2, height: 44, display: 'inline-flex', alignItems: 'center', gap: 4, padding: '0 2px', border: 0, background: 'transparent', color: 'var(--acText)', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                        {t('trips.again')}
                        <Icon name="arrow" size={14} sw={2.4} />
                      </button>
                      <span style={{ fontSize: 17, lineHeight: '22px', fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: ok ? 'var(--ink)' : 'var(--ink3)', textDecoration: ok ? undefined : 'line-through' }}>
                        {rupees(ok ? (r.riderPaysPaise ?? r.farePaise) + r.tipPaise : r.farePaise)}
                      </span>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
          <div className="zr-navspace" />
        </div>
      </div>
      <BottomNav active="trips" />
    </Screen>
  );
}
